import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import LandingPage from "@/pages/LandingPage";
import CompressorPage from "@/pages/CompressorPage";
import CustomerPage from "@/pages/CustomerPage";
import LogbookPage from "@/pages/LogbookPage";
import ProductClassesPage from "@/pages/ProductClassesPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import MyAccountPage from "@/pages/MyAccountPage";
import CompressorsListPage from "@/pages/CompressorsListPage";
import AssistantPage, { AssistantFab as RawAssistantFab } from "@/pages/AssistantPage";
import HelpDrawer from "@/components/help/HelpDrawer";
import CheckoutPage from "@/pages/CheckoutPage";
import QuoteDetailPage from "@/pages/QuoteDetailPage";
import MyAccessPage from "@/pages/MyAccessPage";
import HotLeadsPage from "@/pages/HotLeadsPage";
import ConversationsPage from "@/pages/ConversationsPage";
import ConversationsV2Page from "@/pages/ConversationsV2Page";
import ConvLinkPage from "@/pages/ConvLinkPage";
import TwilioCredentialsPage from "@/pages/TwilioCredentialsPage";
import AgingReportPage from "@/pages/AgingReportPage";
import PaymentCredentialsPage from "@/pages/PaymentCredentialsPage";
import SfuCredentialsPage from "@/pages/SfuCredentialsPage";
import ServiceTechPage from "@/pages/ServiceTechPage";
import ServiceVisitsDashboardPage from "@/pages/ServiceVisitsDashboardPage";
import ServiceTechLeaderboardPage from "@/pages/ServiceTechLeaderboardPage";
import CommissioningPage from "@/pages/CommissioningPage";
import CommissioningHistoryPage from "@/pages/CommissioningHistoryPage";
import AdminAutomationsPage from "@/pages/admin/AdminAutomationsPage";
import CustomerAuxPage from "@/pages/admin/CustomerAuxPage";
import WarrantyMasterDocPage from "@/pages/admin/WarrantyMasterDocPage";
import EmailLimitsPage from "@/pages/admin/EmailLimitsPage";
import QuoteApprovalEmailsPage from "@/pages/admin/QuoteApprovalEmailsPage";
import TestCompanyAdminPage from "@/pages/admin/TestCompanyAdminPage";
import CommissioningReviewsPage from "@/pages/admin/CommissioningReviewsPage";
import DiscountApprovalsPage from "@/pages/admin/DiscountApprovalsPage";
import ConvOpsPage from "@/pages/admin/ConvOpsPage";
import ConvAuditPage from "@/pages/admin/ConvAuditPage";
import ImprovementLogPage from "@/pages/admin/ImprovementLogPage";
import RequestedChangesPage from "@/pages/admin/RequestedChangesPage";
import MyRequestsPage from "@/pages/MyRequestsPage";
import VisibilityCheckPage from "@/pages/VisibilityCheckPage";
import RequestAccessPage from "@/pages/RequestAccessPage";
// iter150AE — Sequencing System (Phase 1A)
import SequencingHomePage        from "@/pages/sequencing/SequencingHomePage";
import SequencingGroupsPage      from "@/pages/sequencing/SequencingGroupsPage";
import SequencingGroupDetailPage from "@/pages/sequencing/SequencingGroupDetailPage";
import SequencingCommissioningPage from "@/pages/sequencing/SequencingCommissioningPage";
import SequencingLiveDashboardPage from "@/pages/sequencing/SequencingLiveDashboardPage";
import SequencingGroupBuilderPage  from "@/pages/sequencing/SequencingGroupBuilderPage";
import SequencingTypesPage       from "@/pages/sequencing/SequencingTypesPage";
import SequencingDisclaimersPage from "@/pages/sequencing/SequencingDisclaimersPage";
import SequencingHolidaysPage    from "@/pages/sequencing/SequencingHolidaysPage";
import SequencingDeviceTemplatesPage from "@/pages/sequencing/SequencingDeviceTemplatesPage";
import SequencingCheatsheetPage   from "@/pages/sequencing/SequencingCheatsheetPage";
import FieldToolsSettingsPage from "@/pages/admin/FieldToolsSettingsPage";
import WarrantyEvidenceRulesPage from "@/pages/admin/WarrantyEvidenceRulesPage";
import WarrantyClaimChatPage from "@/pages/WarrantyClaimChatPage";
import TestQueuePage from "@/pages/admin/TestQueuePage";
import HrAdminPage from "@/pages/admin/HrAdminPage";
import AdminManualsEditor from "@/pages/admin/AdminManualsEditor";
import WhatsNewPage from "@/pages/WhatsNewPage";
import ChangelogConfirmPage from "@/pages/ChangelogConfirmPage";
import ChangelogUnsubscribePage from "@/pages/ChangelogUnsubscribePage";
import DealerScorecardPage from "@/pages/admin/DealerScorecardPage";
import DealerAgreementsPage from "@/pages/admin/DealerAgreementsPage";
import DriveOauthAdminPage from "@/pages/admin/DriveOauthAdminPage";
import PositionsAdminPage from "@/pages/PositionsAdminPage";
import CallRoutingPage from "@/pages/CallRoutingPage";
import WarrantyClaimPage from "@/pages/WarrantyClaimPage";
import WarrantyClaimsAdminPage from "@/pages/WarrantyClaimsAdminPage";
import WarrantyInsightsPage from "@/pages/WarrantyInsightsPage";
import DbStatusMonitor from "@/components/DbStatusMonitor";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmProvider } from "@/hooks/use-confirm";
import { I18nProvider } from "@/lib/i18n";
import HelpPage from "@/pages/HelpPage";
import OnboardingWizardPage from "@/pages/OnboardingWizardPage";
import ManualStatsPage from "@/pages/ManualStatsPage";
import PublicFilesPage from "@/pages/PublicFilesPage";
import WhatsNewModal from "@/components/WhatsNewModal";
import WelcomeToursPrompt from "@/components/WelcomeToursPrompt";
import AllToursPage from "@/pages/AllToursPage";
import FloatingLanguagePicker from "@/components/FloatingLanguagePicker";
import TestCompanyBanner from "@/components/TestCompanyBanner";
import OfflineIndicator from "@/components/OfflineIndicator";
import OutboxToastObserver from "@/components/OutboxToastObserver";
import CartDrawer from "@/components/CartDrawer";
import { CartProvider } from "@/lib/cart";
import { PresenterProvider } from "@/lib/screen-share/PresenterContext";
import { ViewerProvider } from "@/lib/screen-share/ViewerContext";
import { convSocket } from "@/lib/conv/socket";
import { notificationStore } from "@/lib/conv/notificationStore";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { loadBranding } from "@/lib/branding";


// Route-aware wrapper around the global AI Assistant FAB. We hide the
// FAB on chat surfaces (V2 conversations + SMS conversations + the
// guest invite-link page) so the floating button doesn't sit on top
// of the textarea / keyboard on mobile.
function AssistantFab({ user }) {
  const { pathname } = useLocation();
  if (
    pathname.startsWith("/conversations-v2") ||
    pathname.startsWith("/conversations") ||
    pathname.startsWith("/conv-link")
  ) {
    return null;
  }
  return <RawAssistantFab user={user} />;
}
import { canAccessCustomers } from "@/lib/access";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AppRouter({ user, setUser, handleLogout, handleLogoutAll }) {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={setUser} />;
  }

  // Magic-link quote view — /quote/:id?t=<token> is public for anonymous users
  // so that forwarded share-links work without forcing a login.
  const isMagicLinkQuote =
    location.pathname.startsWith("/quote/") &&
    new URLSearchParams(location.search).get("t");

  // /help is public so guests can read the manual without an account.
  const isPublicHelp = location.pathname.startsWith("/help");

  // /conv-link/:token is public — external users redeem their email
  // invite (token + OTP) without a portal account (iter124).
  const isConvLink = location.pathname.startsWith("/conv-link/");

  // /whats-new is the public customer-facing changelog (iter140.5).
  // /whats-new/confirm/:token and /whats-new/unsubscribe/:token are
  // public landing pages for the subscribe-confirm + one-click
  // unsubscribe flow (iter142).
  const isWhatsNew = location.pathname === "/whats-new"
                  || location.pathname.startsWith("/whats-new/confirm/")
                  || location.pathname.startsWith("/whats-new/unsubscribe/");

  if (!user && !isMagicLinkQuote && !isPublicHelp && !isConvLink && !isWhatsNew) return <LoginPage onLogin={setUser} />;

  const isCustomer = user?.role === "customer";
  const customersAllowed = canAccessCustomers(user);
  const CustomersGuard = (El) => customersAllowed ? El : <Navigate to="/us-class" replace />;

  return (
    <Routes>
      <Route path="/" element={isCustomer ? <Navigate to="/my-account" replace /> : <LandingPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} />} />
      <Route path="/my-account" element={isCustomer ? <MyAccountPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/us-class" element={<CompressorPage user={user} onLogout={handleLogout} tab="us-class" />} />
      <Route path="/us-hp" element={<CompressorPage user={user} onLogout={handleLogout} tab="us-hp" />} />
      <Route path="/acc-class" element={<CompressorPage user={user} onLogout={handleLogout} tab="acc-class" />} />
      <Route path="/acc-hp" element={<CompressorPage user={user} onLogout={handleLogout} tab="acc-hp" />} />
      <Route path="/customers-us" element={isCustomer ? <Navigate to="/my-account" replace /> : CustomersGuard(<CustomerPage user={user} onLogout={handleLogout} country="US" />)} />
      <Route path="/customers-ca" element={isCustomer ? <Navigate to="/my-account" replace /> : CustomersGuard(<CustomerPage user={user} onLogout={handleLogout} country="CA" />)} />
      <Route path="/customers-other" element={isCustomer ? <Navigate to="/my-account" replace /> : CustomersGuard(<CustomerPage user={user} onLogout={handleLogout} country="Other" />)} />
      <Route path="/logbook" element={isCustomer ? <Navigate to="/my-account" replace /> : CustomersGuard(<LogbookPage user={user} onLogout={handleLogout} />)} />
      <Route path="/product-classes" element={isCustomer ? <Navigate to="/my-account" replace /> : <ProductClassesPage user={user} onLogout={handleLogout} />} />
      <Route path="/compressors" element={<CompressorsListPage user={user} onLogout={handleLogout} />} />
      <Route path="/compressors/:serialId" element={<CompressorsListPage user={user} onLogout={handleLogout} />} />
      <Route path="/assistant" element={<AssistantPage user={user} onLogout={handleLogout} />} />
      <Route path="/checkout" element={<CheckoutPage user={user} onLogout={handleLogout} />} />
      <Route path="/quote/:id" element={<QuoteDetailPage user={user} onLogout={handleLogout} />} />
      <Route path="/my-access" element={<MyAccessPage user={user} onLogout={handleLogout} />} />
      <Route path="/admin/hot-leads" element={user?.is_admin ? <HotLeadsPage user={user} /> : <Navigate to="/" replace />} />
      <Route path="/conversations" element={isCustomer ? <Navigate to="/my-account" replace /> : CustomersGuard(<ConversationsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} />)} />
      <Route path="/conversations-v2" element={<ConversationsV2Page user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} />} />
      <Route path="/conv-link/:token" element={<ConvLinkPage />} />
      <Route path="/admin/twilio-credentials" element={user?.is_admin ? <TwilioCredentialsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/aging" element={user?.is_admin ? <AgingReportPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/service-visits" element={user?.is_admin ? <ServiceVisitsDashboardPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/service-techs" element={user?.is_admin ? <ServiceTechLeaderboardPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/positions" element={user?.is_admin ? <PositionsAdminPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/call-routing" element={user?.is_admin ? <CallRoutingPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/payment-credentials" element={user?.is_admin ? <PaymentCredentialsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/sfu-credentials" element={user?.is_admin ? <SfuCredentialsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/onboard" element={user?.is_admin ? <OnboardingWizardPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/manual-stats" element={user?.is_admin ? <ManualStatsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/service-tech" element={user?.email ? <ServiceTechPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/login" replace />} />
      <Route path="/commissioning" element={user?.email ? <CommissioningPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/login" replace />} />
      <Route path="/commissioning/history" element={user?.email ? <CommissioningHistoryPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/login" replace />} />
      <Route path="/admin/automations" element={user?.is_admin ? <AdminAutomationsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/customer-aux" element={user?.is_admin ? <CustomerAuxPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/commissioning/:visitId" element={user?.email ? <CommissioningPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/login" replace />} />
      <Route path="/admin/warranty/master-doc" element={user?.is_admin ? <WarrantyMasterDocPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/email-limits" element={user?.is_admin ? <EmailLimitsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/quote-approval-emails" element={user?.is_admin ? <QuoteApprovalEmailsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/test-company" element={user?.is_admin ? <TestCompanyAdminPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/commissioning/reviews" element={user?.is_admin ? <CommissioningReviewsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/commissioning/scorecard" element={user?.is_admin ? <DealerScorecardPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/dealer-agreements" element={user?.is_admin ? <DealerAgreementsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/discount-approvals" element={user?.is_admin ? <DiscountApprovalsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/conv-ops" element={user?.is_admin ? <ConvOpsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/conv-audit" element={user?.is_admin ? <ConvAuditPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/improvement-log" element={user?.is_admin ? <ImprovementLogPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/requested-changes" element={user?.is_admin ? <RequestedChangesPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/my-requests" element={user?.email ? <MyRequestsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/visibility-check" element={user?.email ? <VisibilityCheckPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      {/* iter150AC — self-service "request to be linked to my company" form
          (sg_36203cc895e84a). Accessible to any signed-in user; no role gate
          so customers who got blocked from /my-account can still get here. */}
      <Route path="/request-access" element={user?.email ? <RequestAccessPage user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      {/* iter150AE — Sequencing System (Phase 1A foundation). Open to any
          signed-in user; tenant scope is enforced server-side. Admin-only
          subroutes (types, disclaimers) gate themselves via 403 on the
          underlying API. */}
      <Route path="/sequencing"             element={user?.email ? <SequencingHomePage         user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/groups"      element={user?.email ? <SequencingGroupsPage       user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/groups/new"               element={user?.email ? <SequencingGroupBuilderPage  user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/groups/:gid"  element={user?.email ? <SequencingGroupDetailPage  user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/groups/:gid/commissioning" element={user?.email ? <SequencingCommissioningPage user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/groups/:gid/live"         element={user?.email ? <SequencingLiveDashboardPage user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/types"       element={user?.email ? <SequencingTypesPage        user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/disclaimers" element={user?.email ? <SequencingDisclaimersPage  user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/holidays"    element={user?.email ? <SequencingHolidaysPage     user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/admin/device-templates" element={user?.email ? <SequencingDeviceTemplatesPage user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="/sequencing/cheatsheet" element={<SequencingCheatsheetPage />} />      <Route path="/admin/field-tools" element={user?.is_super_admin ? <FieldToolsSettingsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/warranty/evidence-rules" element={user?.email ? <WarrantyEvidenceRulesPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/login" replace />} />
      <Route path="/warranty/claims/:cid/chat" element={user?.email ? <WarrantyClaimChatPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/login" replace />} />
      <Route path="/admin/test-queue" element={(user?.is_test_admin || user?.is_super_admin) ? <TestQueuePage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/hr" element={(user?.is_hr_admin || user?.is_super_admin) ? <HrAdminPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/manuals" element={(user?.is_admin || user?.is_super_admin) ? <AdminManualsEditor user={user} /> : <Navigate to="/" replace />} />
      <Route path="/whats-new" element={<WhatsNewPage />} />
      <Route path="/whats-new/confirm/:token" element={<ChangelogConfirmPage />} />
      <Route path="/whats-new/unsubscribe/:token" element={<ChangelogUnsubscribePage />} />
      <Route path="/admin/drive-oauth" element={user?.is_admin ? <DriveOauthAdminPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/help" element={<HelpPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} />} />
      <Route path="/help/:role" element={<HelpPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} />} />
      <Route path="/public-files" element={<PublicFilesPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} />} />
      <Route path="/warranty/claim" element={user?.email ? <WarrantyClaimPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/login" replace />} />
      <Route path="/admin/warranty" element={user?.is_admin ? <WarrantyClaimsAdminPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/admin/warranty/insights" element={user?.is_admin ? <WarrantyInsightsPage user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} /> : <Navigate to="/" replace />} />
      <Route path="/auth/callback" element={<AuthCallback onLogin={setUser} />} />
      <Route path="/login" element={<LoginPage onLogin={setUser} />} />
      <Route path="/tours" element={user?.email ? <AllToursPage user={user} /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleSetUser = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem("usair_user", JSON.stringify(userData));
      // iter131: a portal user just authenticated. Drop any stale
      // guest Conv session left behind by an earlier OTP-redeem
      // flow so the api interceptor can rely cleanly on the portal
      // cookie / X-User-Email and avoid sending a bearer that no
      // longer maps to this user.
      try { localStorage.removeItem("usair_conv_session"); } catch { /* ignore */ }
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.warn("Logout request failed (proceeding with local logout):", err?.message);
    }
    setUser(null);
    localStorage.removeItem("usair_user");
    // iter136.7 — drop the IndexedDB chat-history cache so the next
    // user logging in on this device doesn't see anyone else's
    // messages. Best-effort; never blocks the logout flow.
    try {
      const mod = await import("@/lib/conv/messageCacheDB");
      await mod.clearAll();
    } catch (_e) { /* ignore */ }
  }, []);

  const handleLogoutAll = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/auth/logout-all`, {}, { withCredentials: true });
      const count = res?.data?.sessions_revoked ?? 0;
      window.alert(`Signed out of ${count} device${count === 1 ? "" : "s"}.`);
    } catch (err) {
      console.warn("logout-all failed (proceeding with local logout):", err?.message);
      window.alert("Could not sign out of all devices — your local session has been cleared.");
    }
    setUser(null);
    localStorage.removeItem("usair_user");
    try {
      const mod = await import("@/lib/conv/messageCacheDB");
      await mod.clearAll();
    } catch (_e) { /* ignore */ }
  }, []);

  // iter135.12 — Phase B: keep the global multiplexed conv socket
  // started while the user is logged in. ``convSocket.start`` is
  // idempotent (it no-ops if already started for the same email)
  // and survives route changes because it lives on the module
  // singleton, not in this component's lifecycle.
  useEffect(() => {
    if (user?.email) {
      convSocket.start({ email: user.email });
      notificationStore.start(user);
    } else {
      convSocket.stop();
      notificationStore.stop();
    }
  }, [user?.email]);

  // iter137 — Auto-subscribe to Web Push for @-mention notifications
  // (user-choice 4b: only mentions, no general-message push). Done
  // silently in the background ~3s after auth so we don't fire the
  // permission prompt during the boot animation. If the user denies,
  // we record the choice and never re-prompt.
  useEffect(() => {
    if (!user?.email) return;
    const isGuest = String(user.email).endsWith("@local.guest");
    if (isGuest) return;
    if (typeof Notification === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (Notification.permission === "denied") return;
    const declined = (() => {
      try { return localStorage.getItem("conv:push_declined") === "1"; }
      catch { return false; }
    })();
    if (declined) return;
    const tid = setTimeout(async () => {
      try {
        // Only request perm if we don't already have it AND we
        // haven't asked before. The browser's own throttle kicks in
        // after the second decline, but we add belt-and-braces.
        if (Notification.permission === "default") {
          const perm = await Notification.requestPermission();
          if (perm !== "granted") {
            try { localStorage.setItem("conv:push_declined", "1"); } catch { /* noop */ }
            return;
          }
        }
        const mod = await import("@/lib/push");
        const reg = await navigator.serviceWorker.getRegistration("/sw.js")
          || await navigator.serviceWorker.register("/sw.js", { scope: "/" });
        await navigator.serviceWorker.ready;
        const { data: vk } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/push/vapid-public-key`,
          { withCredentials: true },
        );
        if (!vk?.public_key) return;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          // Reuse the same urlBase64ToUint8Array helper from push.js.
          const _toU8 = (b64) => {
            const padding = "=".repeat((4 - (b64.length % 4)) % 4);
            const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
            const raw = window.atob(base64);
            const arr = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
            return arr;
          };
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: _toU8(vk.public_key),
          });
        }
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/push/subscribe`,
          { subscription: sub.toJSON(), topics: ["conv-mention"], user_agent: navigator.userAgent },
          { withCredentials: true },
        );
        // Reference push module so the import isn't tree-shaken.
        void mod;
      } catch { /* best-effort — silently ignore */ }
    }, 3000);
    return () => clearTimeout(tid);
  }, [user?.email]);

  // iter136.28 — On boot, fetch the dynamic PWA branding and update
  // ``document.title`` + the apple home-screen meta + the theme-color
  // meta whenever the cache changes. The branding module owns the
  // single fetch; we just subscribe so the metas track admin saves
  // live (no page refresh needed).
  useEffect(() => {
    const apply = (b) => {
      if (!b || b.loading) return;
      if (b.title) document.title = b.title;
      const setMeta = (name, value) => {
        if (!value) return;
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute("name", name);
          document.head.appendChild(el);
        }
        el.setAttribute("content", value);
      };
      setMeta("apple-mobile-web-app-title", b.short_name || b.title);
      setMeta("theme-color", b.theme_color);
      setMeta("description", b.description);
    };
    // Apply current cached state immediately, then re-apply on changes.
    apply(loadBranding());
    let cancelled = false;
    const tick = () => { if (!cancelled) apply(loadBranding()); };
    // Subscribe to module-level pub-sub via a tiny useState force-render
    // pattern — wrap-around: poll once after fetch resolves.
    const unsubMem = (() => {
      // _listeners is internal to branding.js, so use the public hook trick:
      // subscribe via DOM event we'll dispatch from refreshBranding.
      const handler = () => tick();
      window.addEventListener("usair:branding-changed", handler);
      return () => window.removeEventListener("usair:branding-changed", handler);
    })();
    // Also kick once after first paint in case the fetch resolves later.
    const tid = setTimeout(tick, 50);
    const tid2 = setTimeout(tick, 600);
    return () => { cancelled = true; clearTimeout(tid); clearTimeout(tid2); unsubMem(); };
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Try to restore session from cookie first, then localStorage
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        handleSetUser(res.data);
      })
      .catch(() => {
        // Fallback to localStorage
        const saved = localStorage.getItem("usair_user");
        if (saved) {
          setUser(JSON.parse(saved));
          // iter131: same cleanup as handleSetUser — a portal user
          // restored from cache must not carry a stale guest token.
          try { localStorage.removeItem("usair_conv_session"); } catch { /* ignore */ }
        }
      })
      .finally(() => setLoading(false));
  }, [handleSetUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="App">
      <I18nProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <CartProvider>
          <PresenterProvider>
            <ViewerProvider>
              <TestCompanyBanner user={user} />
              <AppRouter user={user} setUser={handleSetUser} handleLogout={handleLogout} handleLogoutAll={handleLogoutAll} />
              <DbStatusMonitor />
              <AssistantFab user={user} />
              {/* iter150AG Part 26 — app-wide help drawer ("?" floats bottom-right). */}
              {user && <HelpDrawer />}
              <WhatsNewModal user={user} />
              <WelcomeToursPrompt user={user} />
              <CartDrawer />
              <FloatingLanguagePicker user={user} />
              <OfflineIndicator />
              <OutboxToastObserver />
              <Toaster richColors closeButton position="top-right" />
            </ViewerProvider>
          </PresenterProvider>
        </CartProvider>
      </BrowserRouter>
    </ConfirmProvider>
    </I18nProvider>
    </div>
  );
}

export default App;
